import { getTickerForCurrency, type Quote } from "features/market/lib/quotes";
import type { QuoteCurrency } from "features/market/lib/marketQuotes";
import type { PanelType } from "features/market/api/MarketQuotes";

export const QUOTE_GUIDE_PANELS: PanelType[] = ["cedears", "acciones"];

const IMPORTANT_SYMBOLS = new Set([
  "AAPL",
  "AMZN",
  "GOOGL",
  "META",
  "MELI",
  "MSFT",
  "NVDA",
  "QQQ",
  "SPY",
  "TSLA",
]);

export interface ImportantMarketMoveConfig {
  minAbsVariation: number;
  minVolumeForNonWatchlist: number;
}

export const IMPORTANT_MARKET_MOVE_CONFIG: ImportantMarketMoveConfig = {
  minAbsVariation: 10,
  minVolumeForNonWatchlist: 500_000_000,
};

export interface QuoteGuidePanelData {
  panel: PanelType;
  quotes: Quote[];
}

export interface ImportantMarketMove {
  panel: PanelType;
  symbol: string;
  name: string;
  variation: number;
  volume: number | null;
  lastPrice: number | null;
  isWatchlistSymbol: boolean;
}

function symbolCandidates(symbol: string): string[] {
  const normalized = String(symbol || "").trim().toUpperCase();
  if (!normalized) return [];
  return [
    normalized,
    normalized.replace(/DD$/, ""),
    normalized.replace(/D$/, ""),
    normalized.replace(/C$/, ""),
  ].filter(Boolean);
}

function isWatchlistSymbol(symbol: string): boolean {
  return symbolCandidates(symbol).some((candidate) => IMPORTANT_SYMBOLS.has(candidate));
}

function moveScore(move: ImportantMarketMove): number {
  const volumeScore = move.volume != null && move.volume > 0 ? Math.log10(move.volume) : 0;
  const watchlistBoost = move.isWatchlistSymbol ? 100 : 0;
  return watchlistBoost + Math.abs(move.variation) * 10 + volumeScore;
}

export function findImportantMarketMove(
  panels: QuoteGuidePanelData[],
  quoteCurrency: QuoteCurrency,
  config = IMPORTANT_MARKET_MOVE_CONFIG,
): ImportantMarketMove | null {
  let best: ImportantMarketMove | null = null;

  for (const panel of panels) {
    for (const quote of panel.quotes) {
      const variant = getTickerForCurrency(quote, quoteCurrency);
      if (!variant || variant.variation == null) continue;

      const absVariation = Math.abs(variant.variation);
      if (absVariation < config.minAbsVariation) continue;

      const watched = isWatchlistSymbol(variant.ticker);
      const enoughVolume =
        variant.volume != null && variant.volume >= config.minVolumeForNonWatchlist;

      if (!watched && !enoughVolume) continue;

      const move: ImportantMarketMove = {
        panel: panel.panel,
        symbol: variant.ticker,
        name: quote.name,
        variation: variant.variation,
        volume: variant.volume,
        lastPrice: variant.lastPrice,
        isWatchlistSymbol: watched,
      };

      if (!best || moveScore(move) > moveScore(best)) {
        best = move;
      }
    }
  }

  return best;
}
